import { useSnapshot } from 'valtio';
import { createSimpleAsyncAction } from 'async-utils';
import { logger } from 'logging-utils';
import { loadIrrelevantApps } from '../../irrelevant-apps/actions/irrelevant-apps-actions';
import {
  closeIssueModal,
  closeIssueModalWithoutNavigate,
  setApplicationIrrelevantModal,
  setPolicyDisableModal,
} from '../../issues/active-issues/store-actions/issue-store-actions';
import { setExcludeIssueModal } from '../../issues/common/store-actions/issues-exclusions-store-actions';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import {
  ExcludeAlertInput,
  ExcludeAppInput,
  ExcludeInput,
  ExclusionsOrder,
  ExclusionSortKey,
  LoadExclusionssParams,
  UpdateExpiredAtByIdInput,
} from '../types/exclusion-types';
import exclusionsService from '../services';
import { RemoveAlertExclusionInput } from '../services/remove-alert-exclusion/remove-alert-exclusion';
import { RemoveAppExclusionInput } from '../services/remove-app-exclusion/remove-app-exclusion';
import { RemovePolicyExclusionInput } from '../services/remove-policy-exclusion/remove-policy-exclusion';

import {
  setBannerSnoozeDay,
  setBannerSnoozeWeek,
  setExclusions,
  setIsApplicationIrrelevantLoading,
  setIsDisablePolicyLoading,
  setIsExcludeAlertLoading,
  setIsLoadingRemoveSnooze,
  setIsRemoveExclusionLoading,
  setIsUpdateExpiredAtByIdLoading,
  setModalAction,
  setModalRemoveSnooze,
  setModalUpdateExpiredAt,
  setTotalExclusionsFiltersNumber,
  setTotalExclusionsNumber,
} from '../store-actions/exclusions-store-actions';
import ExclusionsStore from '../stores/exclusions-store';
import {
  findExpiredSnoozeWeek,
  snackBarSpplitingName,
} from '../utils/exclusion-utils';
import {
  loadFilters,
  loadIssues,
} from '../../issues/active-issues/actions/active-issues-actions';
import { loadInventory } from '../../dashboard-inventory/actions/inventory-actions';
import {
  loadFiltersPipeline,
  loadIssuesPipeline,
} from '../../issues/pipeline-issues/actions/pipeline-issues-actions';
import { loadDashboardInfo } from '../../executive-summary/actions/executive-summary-actions';
import { isValidDate } from '../../api-key-settings/utils/date-utils';
import { clearFilters } from './exclusions-filters-actions';

export const loadExclusions = createSimpleAsyncAction(
  async (params?: LoadExclusionssParams) => {
    const { update = false } = params || {};
    const result = await exclusionsService.getExclusions.execute(
      ExclusionsStore.filterExclusionsBy,
    );

    if (result) {
      setTotalExclusionsFiltersNumber(result.totalFilteredExclusions);
      setExclusions(result.exclusions, update);
      setTotalExclusionsNumber(result.totalExclusions);
      setBannerSnoozeDay(findExpiredSnoozeWeek(result.exclusions));
      setBannerSnoozeWeek(findExpiredSnoozeWeek(result.exclusions));
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to load exclusions',
  },
);

export const onSort = (key: ExclusionSortKey) => {
  ExclusionsStore.orderBy = key;
  ExclusionsStore.order =
    ExclusionsStore.order === ExclusionsOrder.Asc
      ? ExclusionsOrder.Desc
      : ExclusionsOrder.Asc;
  loadExclusions();
};

export const excludeIrrelevantApp = createSimpleAsyncAction(
  async (input: ExcludeInput | ExcludeAppInput) => {
    try {
      setIsApplicationIrrelevantLoading(true);

      const result = await exclusionsService.excludeApplications.execute([
        input,
      ]);
      if (result) {
        openSnackbar(
          `The following app was made irrelevant: ${snackBarSpplitingName(
            input.appName,
          )}`,
          {
            variant: 'success',
          },
        );
        setIsApplicationIrrelevantLoading(false);
        loadExclusions();
        setApplicationIrrelevantModal(false);
        //setApplicationIrrelevantModalDeshboard(false);
        closeIssueModalWithoutNavigate();
        loadIssues({ cache: false, exclude: true });
        loadIrrelevantApps();
        loadFilters();
        loadInventory();
        loadDashboardInfo();
      }
    } catch (err) {
      logger.error('Failed to load irrelevant app', err);
      setApplicationIrrelevantModal(false);
      openSnackbar(
        `There was a problem to make this app irrelevant: ${snackBarSpplitingName(
          input.appName,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to exclude irrelevant app',
  },
);

export const excludeDisabledPolicy = createSimpleAsyncAction(
  async (input: ExcludeInput) => {
    try {
      setIsDisablePolicyLoading(true);
      const result = await exclusionsService.excludePolicies.execute(input);
      if (result) {
        openSnackbar(
          `The following policy has been disabled: ${snackBarSpplitingName(
            input.policyName,
          )}`,
          { variant: 'success' },
        );
        setIsDisablePolicyLoading(false);
        setPolicyDisableModal(false);
        loadExclusions();
        closeIssueModal();
        loadIssues({ cache: false, exclude: true });
        loadFilters();
        loadInventory();
      }
    } catch (err) {
      logger.error('Failed to load disable policy', err);
      setIsApplicationIrrelevantLoading(false);
      setIsDisablePolicyLoading(false);
      setPolicyDisableModal(false);
      openSnackbar(
        `There was an error in disabling the policy: ${snackBarSpplitingName(
          input.policyName,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to exclude disabled policy',
  },
);

export const excludeAlert = createSimpleAsyncAction(
  async (input: ExcludeAlertInput) => {
    try {
      if (input.expiredAt && !isValidDate(input.expiredAt)) {
        logger.error(`There was an error to change the expire date`);
        setIsUpdateExpiredAtByIdLoading(false);
        openSnackbar(`There was an error to change the expire date`, {
          variant: 'error',
        });
        return false;
      }
      setIsExcludeAlertLoading(true);
      const result = await exclusionsService.exludeAlert.execute(input);
      if (result) {
        setIsExcludeAlertLoading(false);
        loadExclusions();
        setExcludeIssueModal(false);
        loadIssues({ cache: false, exclude: true });
        loadFilters();
        loadIssuesPipeline({ update: true, cache: false, exclude: true });
        loadFiltersPipeline();
        closeIssueModal();
        loadInventory();
        loadDashboardInfo();
        openSnackbar(
          `The following issue has been excluded: ${snackBarSpplitingName(
            input.issueName,
          )}`,
          {
            variant: 'success',
          },
        );
      }
    } catch (err) {
      logger.error('Failed to load exclued issue', err);
      setIsExcludeAlertLoading(false);
      setExcludeIssueModal(false);
      openSnackbar(
        `There was an error excluding the issue: ${snackBarSpplitingName(
          input.issueName,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to exclude alert',
  },
);

export const removePolicyExclusion = createSimpleAsyncAction(
  async (input: RemovePolicyExclusionInput) => {
    try {
      setIsRemoveExclusionLoading(true);
      const result = await exclusionsService.removePolicyExclusion.execute(
        input,
      );
      if (result) {
        setModalAction(false);
        setExclusions([]);
        loadExclusions();
        setIsRemoveExclusionLoading(false);
        loadInventory();
        openSnackbar(
          `The following policy has been enabled: ${snackBarSpplitingName(
            input.policyName,
          )}`,
          {
            variant: 'success',
          },
        );
      }
    } catch (e) {
      logger.error(
        `There was an error enabling this policy: ${snackBarSpplitingName(
          input.policyName,
        )}`,
      );
      setIsRemoveExclusionLoading(false);
      setModalAction(false);
      openSnackbar(
        `There was an error enabling this policy: ${snackBarSpplitingName(
          input.policyName,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to remove policy exclusion',
  },
);

export const removeAlertExclusion = createSimpleAsyncAction(
  async (input: RemoveAlertExclusionInput) => {
    try {
      setIsRemoveExclusionLoading(true);
      const result = await exclusionsService.removeAlertExclusion.execute(
        input,
      );
      if (result) {
        setModalAction(false);
        setExclusions([]);
        clearFilters();
        loadExclusions({ update: true });
        setIsRemoveExclusionLoading(false);
        loadInventory();
        openSnackbar(
          `The following issue exclusion has been removed: ${snackBarSpplitingName(
            input.issueName,
          )}`,
          {
            variant: 'success',
          },
        );
      }
    } catch (e) {
      logger.error(
        `There was an error removing exclusion: ${snackBarSpplitingName(
          input.issueName,
        )}`,
      );
      setIsRemoveExclusionLoading(false);
      setModalAction(false);
      openSnackbar(
        `There was an error removing exclusion: ${snackBarSpplitingName(
          input.issueName,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to remove alert exclusion',
  },
);

export const removeAppExclusion = createSimpleAsyncAction(
  async (input: RemoveAppExclusionInput) => {
    try {
      setIsRemoveExclusionLoading(true);
      const result = await exclusionsService.removeAppExclusion.execute(input);
      if (result.isSucceded) {
        setIsRemoveExclusionLoading(false);
        setModalAction(false);
        setExclusions([]);
        clearFilters();
        loadExclusions({ update: true });
        loadInventory();
        openSnackbar(
          `The following application was made relevant: ${snackBarSpplitingName(
            input.appName,
          )}`,
          {
            variant: 'success',
          },
        );
      }
    } catch (e) {
      logger.error(
        `There was an error making this app relevant: ${snackBarSpplitingName(
          input.appName,
        )}`,
      );
      setIsRemoveExclusionLoading(false);
      openSnackbar(
        `There was an error making this app relevant: ${snackBarSpplitingName(
          input.appName,
        )}`,
        {
          variant: 'error',
        },
      );
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to remove app exclusion',
  },
);

export const updateExpiredAtById = createSimpleAsyncAction(
  async (input: UpdateExpiredAtByIdInput) => {
    try {
      if (input.expiredAt && !isValidDate(input.expiredAt)) {
        logger.error(`There was an error to change the expire date`);
        setIsUpdateExpiredAtByIdLoading(false);
        openSnackbar(`There was an error to change the expire date`, {
          variant: 'error',
        });
        return false;
      }
      setIsUpdateExpiredAtByIdLoading(true);
      const result = await exclusionsService.updateExpiredAtById.execute(input);
      if (result) {
        setIsUpdateExpiredAtByIdLoading(false);
        setModalUpdateExpiredAt(false);
        clearFilters();
        loadExclusions({ update: true });
        loadInventory();
        openSnackbar(
          `The expire date changed to: ${
            input.expiredAt ? input.expiredAt.toLocaleDateString() : 'Never'
          }`,
          {
            variant: 'success',
          },
        );
      }
    } catch (e) {
      logger.error(`There was an error to change the expire date`, e);
      setIsUpdateExpiredAtByIdLoading(false);
      openSnackbar(`There was an error to change the expire date`, {
        variant: 'error',
      });
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to remove app exclusion',
  },
);

export const removeSnoozeIssue = createSimpleAsyncAction(
  async (input: UpdateExpiredAtByIdInput) => {
    try {
      setIsLoadingRemoveSnooze(true);
      const result = await exclusionsService.updateExpiredAtById.execute(input);
      if (result) {
        setIsLoadingRemoveSnooze(false);
        setModalRemoveSnooze(false);
        clearFilters();
        loadExclusions({ update: true });
        loadInventory();
        openSnackbar(`Snooze removed from issue`, {
          variant: 'success',
        });
      }
    } catch (e) {
      setIsLoadingRemoveSnooze(false);
      logger.error(`There was an error to remove snooze`, e);
      setIsUpdateExpiredAtByIdLoading(false);
      openSnackbar(`There was an error to remove snooze`, {
        variant: 'error',
      });
    }
  },
  {
    asyncState: ExclusionsStore.loading,
    errorMessage: 'Failed to remove app exclusion',
  },
);

export const clearStore = () => {
  ExclusionsStore.exclusions = [];
  ExclusionsStore.filterExclusionsBy = {};
  ExclusionsStore.totalExclusions = 0;
  ExclusionsStore.totalFilteredExclusions = 0;
};

export const useExclusions = () => useSnapshot(ExclusionsStore);
